import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import api from '../services/api';
import './Subsecretarias.css';

const Subsecretarias = () => {
  const [subsecretarias, setSubsecretarias] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    id_secretaria: '',
    sigla: '',
    descricao: '',
    status: 'ativo'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [subsecretariasRes, secretariasRes] = await Promise.all([
        api.get('/subsecretarias'),
        api.get('/secretarias')
      ]);

      if (subsecretariasRes.data.success) {
        // Organizar subsecretarias por sigla da secretaria
        const subsecretariasOrdenadas = subsecretariasRes.data.data.sort((a, b) => {
          const siglaA = a.secretaria?.sigla || '';
          const siglaB = b.secretaria?.sigla || '';
          if (siglaA !== siglaB) {
            return siglaA.localeCompare(siglaB);
          }
          // Se mesma secretaria, ordenar por sigla da subsecretaria
          return (a.sigla || '').localeCompare(b.sigla || '');
        });
        setSubsecretarias(subsecretariasOrdenadas);
      }
      if (secretariasRes.data.success) {
        setSecretarias(secretariasRes.data.data);
        if (secretariasRes.data.data.length > 0 && !formData.id_secretaria) {
          setFormData(prev => ({ ...prev, id_secretaria: secretariasRes.data.data[0].id_secretaria }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await api.put(`/subsecretarias/${editing.id_subsecretaria}`, formData);
        setSuccess('Subsecretaria atualizada com sucesso!');
      } else {
        await api.post('/subsecretarias', formData);
        setSuccess('Subsecretaria criada com sucesso!');
      }

      setShowModal(false);
      setEditing(null);
      setFormData({
        id_secretaria: secretarias.length > 0 ? secretarias[0].id_secretaria : '',
        sigla: '',
        descricao: '',
        status: 'ativo'
      });
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar subsecretaria');
    }
  };

  const handleEdit = (subsecretaria) => {
    setEditing(subsecretaria);
    setFormData({
      id_secretaria: subsecretaria.id_secretaria,
      sigla: subsecretaria.sigla,
      descricao: subsecretaria.descricao,
      status: subsecretaria.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta subsecretaria?')) {
      return;
    }

    try {
      await api.delete(`/subsecretarias/${id}`);
      setSuccess('Subsecretaria excluída com sucesso!');
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir subsecretaria');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      id_secretaria: secretarias.length > 0 ? secretarias[0].id_secretaria : '',
      sigla: '',
      descricao: '',
      status: 'ativo'
    });
  };

  if (loading) {
    return (
      <div className="subsecretarias-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="subsecretarias-container">
      <div className="subsecretarias-header">
        <h1>Subsecretarias</h1>
        <button 
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Nova Subsecretaria
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="subsecretarias-table-container">
        <table className="subsecretarias-table">
          <thead>
            <tr>
              <th>Cidade</th>
              <th>Secretaria</th>
              <th>Sigla</th>
              <th>Descrição</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {subsecretarias.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Nenhuma subsecretaria cadastrada
                </td>
              </tr>
            ) : (
              (() => {
                // Agrupar subsecretarias por secretaria
                const subsecretariasPorSecretaria = {};
                subsecretarias.forEach(sub => {
                  const siglaSecretaria = sub.secretaria?.sigla || 'GERAL';
                  if (!subsecretariasPorSecretaria[siglaSecretaria]) {
                    subsecretariasPorSecretaria[siglaSecretaria] = [];
                  }
                  subsecretariasPorSecretaria[siglaSecretaria].push(sub);
                });
                
                // Ordenar secretarias por sigla
                const secretariasOrdenadas = Object.keys(subsecretariasPorSecretaria).sort((a, b) => 
                  a.localeCompare(b)
                );
                
                // Renderizar agrupado por secretaria
                return secretariasOrdenadas.map(siglaSecretaria => {
                  const subs = subsecretariasPorSecretaria[siglaSecretaria];
                  const primeiraSub = subs[0];
                  
                  return (
                    <React.Fragment key={siglaSecretaria}>
                      {subs.map((subsecretaria, index) => (
                        <tr key={subsecretaria.id_subsecretaria}>
                          {index === 0 && (
                            <>
                              <td rowSpan={subs.length} style={{ verticalAlign: 'top', fontWeight: 600, backgroundColor: '#F9FAFB' }}>
                                {subsecretaria.secretaria?.cidade?.sigla || '-'}
                              </td>
                              <td rowSpan={subs.length} style={{ verticalAlign: 'top', fontWeight: 600, backgroundColor: '#F9FAFB' }}>
                                {siglaSecretaria}
                              </td>
                            </>
                          )}
                          <td><strong>{subsecretaria.sigla}</strong></td>
                          <td>{subsecretaria.descricao}</td>
                          <td>
                            <span className={`status-badge status-${subsecretaria.status}`}>
                              {subsecretaria.status}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => handleEdit(subsecretaria)}
                              >
                                Editar
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDelete(subsecretaria.id_subsecretaria)}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                });
              })()
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Subsecretaria' : 'Nova Subsecretaria'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="id_secretaria">Secretaria *</label>
                <select
                  id="id_secretaria"
                  value={formData.id_secretaria}
                  onChange={(e) => setFormData({ ...formData, id_secretaria: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="">Selecione uma secretaria</option>
                  {secretarias.map((secretaria) => (
                    <option key={secretaria.id_secretaria} value={secretaria.id_secretaria}>
                      {secretaria.cidade?.sigla} - {secretaria.sigla} - {secretaria.descricao}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="sigla">Sigla *</label>
                <input
                  type="text"
                  id="sigla"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value.toUpperCase() })}
                  required
                  maxLength={20}
                  className="form-input"
                  placeholder="Ex: AED"
                />
              </div>

              <div className="form-group">
                <label htmlFor="descricao">Descrição *</label>
                <input
                  type="text"
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  maxLength={150}
                  className="form-input"
                  placeholder="Ex: Educação a Distância"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subsecretarias;

